import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { NoAuthGuard } from './core/guards/no-auth.guard';
import { featureGuard } from './core/guards/feature.guard';
import { gestorGuard } from './core/guards/gestor.guard';
import { speiProviderGuard } from './core/guards/spei-provider.guard';
import { ecommerceGuard } from './core/guards/ecommerce.guard';
import { PreventBackToLoginGuard } from './core/guards/prevent-back-to-login.guard';
import { environment } from '../environments/environment';

// Rutas normales de la aplicacion
const appRoutes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login.component').then((m) => m.LoginComponent),
    canActivate: [NoAuthGuard]
  },
  {
    path: 'verify-code',
    loadComponent: () =>
      import(/* webpackChunkName: "verify-code" */ './pages/verify-code/verify-code.component').then(
        (m) => m.VerifyCodeComponent
      ),
    canActivate: [NoAuthGuard],
    canDeactivate: [PreventBackToLoginGuard],
  },

  {
    // Pantalla posterior al login: elegir producto. Solo aparece con MAS de un
    // producto; con uno solo, verify-code aterriza directo en su dashboard.
    path: 'products',
    loadComponent: () =>
      import(/* webpackChunkName: "product-selection" */ './pages/product-selection/product-selection.component').then(
        (m) => m.ProductSelectionComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'account-selection',
    loadComponent: () =>
      import(/* webpackChunkName: "account-selection" */ './pages/account-selection/account-selection.component').then(
        (m) => m.AccountSelectionComponent
      ),
    // canActivate: [NoAuthGuard], // Removed to allow navigation from authenticated state
  },
  {
    path: 'authentication',
    loadComponent: () =>
      import(/* webpackChunkName: "authentication" */ './pages/authentication/authentication.component').then(
        (m) => m.AuthenticationComponent
      ),
    canActivate: [NoAuthGuard]
  },
  {
    path: 'create-new-password',
    loadComponent: () =>
      import(/* webpackChunkName: "create-new-password" */ './pages/create-new-password/create-new-password.component').then(
        (m) => m.CreateNewPasswordComponent
      ),
    canActivate: [NoAuthGuard]
  },
  {
    path: 'recovery-password',
    loadComponent: () =>
      import('./pages/recovery-password/recovery-password.component').then(
        (m) => m.RecoveryPasswordComponent
      ),
    canActivate: [NoAuthGuard]
  },


  {
    path: 'onboarding',
    loadChildren: () =>
      import('./pages/onboarding/onboarding.module').then(
        (m) => m.OnboardingModule
      ),
  },
  {
    path: 'onboarding-desk',
    loadComponent: () =>
      import('./pages/onboarding-desk/onboarding-desk.component').then(
        (m) => m.OnboardingDeskComponent
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'verify-identify',
    loadComponent: () =>
      import('./pages/verify-identify/verify-identify.component').then(
        (m) => m.VerifyIdentifyComponent
      ),
    canActivate: [NoAuthGuard]
  },
  {
    path: 'cards/kyb',
    loadChildren: () =>
      import('./cards/pages/kyb/kyb.module').then((m) => m.KybModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'cards/kyc',
    loadChildren: () =>
      import('./cards/pages/kyc/kyc.module').then((m) => m.KycModule),
    canActivate: [AuthGuard],
  },
  {
  // Default redirect for authenticated users
    path: '',
    loadComponent: () =>
      import('./shared/components/main/main.component').then(
        (m) => m.MainComponent
      ),
    //canActivate: [AuthGuard], // Moved to individual child routes
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-home/dashboard-home.component').then(
            (m) => m.DashboardHomeComponent
          ),
        data: {
          breadcrumb: 'shell.breadcrumbs.dashboard',
          // host (feature 108): pasa con 2+ líneas (Todo) o gestor SPEI estricto (Envíos);
          // si no, el guard lleva a la entrada de dashboard del usuario (solo-TPV →
          // /tpv/dashboard, solo-PAYMENTS → Pasarela de Pagos, solo-Tarjetas → /cards/dashboard).
          requiresGestor: 'dashboard',
          // host: 'auto' decide por líneas de negocio (2+ → Todo; una → su vista)
          initialView: 'auto'
        },
        canActivate: [AuthGuard, gestorGuard]
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/user-profile/user-profile.component').then(
            (m) => m.UserProfileComponent
          ),
        data: {
          breadcrumb: 'shell.breadcrumbs.userProfile'
        },
        canActivate: [AuthGuard] // Apply AuthGuard here
      },
      {
        path: 'login-history',
        loadComponent: () => import('./shared/components/login-history/login-history.component').then((m) => m.LoginHistoryComponent),
        data: {
          breadcrumb: 'shell.breadcrumbs.loginHistory'
        },
        canActivate: [AuthGuard] // Apply AuthGuard here
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications.component').then(
            (m) => m.NotificationsComponent
          ),
        data: {
          breadcrumb: 'shell.breadcrumbs.allNotifications'
        },
        canActivate: [AuthGuard] // Apply AuthGuard here
      },
      {
        path: 'treasury-table',
        loadComponent: () =>
          import('./pages/treasury-table/treasury-table.component').then(
            (m) => m.TreasuryTableComponent
          ),
        data: {
          breadcrumb: 'shell.breadcrumbs.balanceStatus'
        },
        canActivate: [AuthGuard] // Apply AuthGuard here
      },
      {
        path: 'entities',
        loadComponent: () =>
          import('./pages/entities/entities.component').then(
            (m) => m.EntitiesComponent
          ),
        data: { breadcrumb: 'entities.title', featureFlag: 'entities', requiresGestor: 'spei' },
        canActivate: [AuthGuard, featureGuard, gestorGuard, speiProviderGuard]
      },
      {
        path: 'transactions',
        loadChildren: () =>
          import('./transactions/transactions.module').then(
            (m) => m.TransactionsModule
          ),
        data: { featureFlag: 'transactions', requiresGestor: 'spei' },
        canActivate: [AuthGuard, featureGuard, gestorGuard, speiProviderGuard]
      },
      {
        path: 'fondeo',
        loadComponent: () =>
          import('./pages/fondeo/fondeo.component').then(
            (m) => m.FondeoComponent
          ),
        data: { breadcrumb: 'fondeo.title', requiresGestor: 'spei' },
        canActivate: [AuthGuard, gestorGuard, speiProviderGuard]
      },
      {
        path: 'cards',
        loadChildren: () =>
          import('./cards/cards.module').then(
            (m) => m.CardsModule
          ),
        data: { featureFlag: 'cards' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'e-commerce',
        loadChildren: () =>
          import('./e-commerce/e-commerce.module').then(
            (m) => m.ECommerceModule
          ),
        // Gestor PAYMENTS + kill-switch del ambiente; NO el flag del tenant (ver ecommerceGuard).
        canActivate: [AuthGuard, ecommerceGuard]
      },
      {
        path: 'polipay-qr',
        loadChildren: () =>
          import('./polipay-qr/polipay-qr.module').then(
            (m) => m.PolipayQrModule
          ),
        data: { featureFlag: 'polipayQR' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'tpv',
        loadChildren: () =>
          import('./tpv/tpv.module').then(
            (m) => m.TpvModule
          ),
        data: { featureFlag: 'tpv', requiresGestor: 'tpv' },
        canActivate: [AuthGuard, featureGuard, gestorGuard]
      },
      {
        path: 'reference-payments',
        loadChildren: () =>
          import('./reference-payments/reference-payments.module').then(
            (m) => m.ReferencePaymentsModule
          ),
        data: { featureFlag: 'referencePayments' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'qr-wallet',
        loadChildren: () =>
          import('./qr-wallet/qr-wallet.module').then(
            (m) => m.QrWalletModule
          ),
        data: { featureFlag: 'qrWallet' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'administration',
        loadChildren: () =>
          import('./administration/administration.module').then(
            (m) => m.AdministrationModule
          ),
        data: { featureFlag: 'administration' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'dispertions',
        loadChildren: () =>
          import('./dispertions/dispertions.module').then(
            (m) => m.DispertionsModule
          ),
        data: { featureFlag: 'dispersions' },
        canActivate: [AuthGuard, featureGuard]
      },
      {
        path: 'enterprise',
        loadChildren: () =>
          import('./enterprise/enterprise.module').then(
            (m) => m.EnterpriseModule
          ),
        // Drill-down SPEI de empresa/unidad (lee el contexto de AvailableAmountService);
        // ninguna otra línea entra aquí. Mismas guardas que las demás pantallas SPEI.
        data: { featureFlag: 'enterprise', requiresGestor: 'spei' },
        canActivate: [AuthGuard, featureGuard, gestorGuard, speiProviderGuard]
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
    // canActivate: [AuthGuard], // Removed due to invalid configuration with redirectTo
  },
];

// Rutas de mantenimiento: bloquean toda la app
const maintenanceRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./shared/components/maintenance-screen/maintenance-screen.component').then(
        (m) => m.MaintenanceScreenComponent
      ),
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '',
  },
];

export const routes: Routes = environment.maintenanceMode ? maintenanceRoutes : appRoutes;